import { useCallback, useEffect, useMemo, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Checkbox, Divider, Icon, Input, Loader, Modal, Tab } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import styled from "styled-components"

import api from "api"
import RoleTable from "components/lib/Roles/RoleTable"
import util from "utils/utils"

import RoleEditorAssignment from "./Assignment"
import RoleEditorPermissions, {
  PermissionCheckbox,
  PermissionDetails,
  PermissionDiv,
  PermissionMeta,
  PermissionTitle,
} from "./Permissions"
import RoleEditorSettings from "./Settings"

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  margin-bottom: 10px;
  gap: 10px;
`

type Permission = {
  key: OpenAPI.Schemas["Role"]["permissions"][number]
  title: string
  text: string
}

export type PermissionGroup = {
  title: string
  permissions: Permission[]
}

type RoleEditorProps = {
  forType: OpenAPI.Schemas["Role"]["forType"]
  permissionOptions: PermissionGroup[]
  forId: string
  canCreateNewRoles?: boolean
  canSetRolesAsDefault?: boolean
}

const RoleEditor = ({
  forType,
  forId,
  permissionOptions,
  canCreateNewRoles,
  canSetRolesAsDefault,
}: RoleEditorProps) => {
  // Updating roles state
  const [roles, setRoles] = useState<OpenAPI.Schemas["Role"][]>([])
  const [managingPermissionsForRoleModalId, setManagingPermissionsForRoleModalId] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [deletingRole, setDeletingRole] = useState<string>("")
  const { t } = useTranslation()

  const [roleNameSearch, setRoleNameSearch] = useState("")
  const [rolePermissionSearch, setRolePermissionSearch] = useState("")

  const filteredRoles = useMemo(() => {
    const nameSearch = roleNameSearch.toLowerCase()
    const permissionSearch = rolePermissionSearch.toLowerCase()

    if (!nameSearch && !permissionSearch) {
      return roles
    }

    return roles.filter((role) => {
      const nameMatch = role.name.toLowerCase().includes(nameSearch)
      if (nameSearch && !nameMatch) {
        return false
      }
      if (!permissionSearch) {
        return true
      }
      for (const permission of role.permissions) {
        if (permission.toLowerCase().includes(permissionSearch)) {
          return true
        }
      }

      return false
    })
  }, [roleNameSearch, rolePermissionSearch, roles])

  // Creating new role state
  const [roleCreationModalOpen, setRoleCreationModalOpen] = useState<boolean>(false)
  const [creatingRole, setCreatingRole] = useState<boolean>(false)
  const [newRoleName, setNewRoleName] = useState<string>("")
  const [newRolePermissions, setNewRolePermissions] = useState<OpenAPI.Schemas["Role"]["permissions"]>([])

  const usingForId = forType === "global" ? "global" : forId
  const managingRoleModalRole = useMemo(
    () => roles.find((role) => role._id === managingPermissionsForRoleModalId),
    [managingPermissionsForRoleModalId, roles],
  )
  const [permissionsTabActive, setPermissionsTabActive] = useState(false)

  const addPermissionsToRole = useCallback(
    (roleId: string, permissions: string[]) => {
      api.roles.addPermissionsToRole(
        forType,
        usingForId,
        roleId,
        permissions,
        ({ role: updatedRole }) => {
          setRoles((prevRoles) =>
            prevRoles.map((role) => (role._id === updatedRole._id ? { ...role, ...updatedRole } : role)),
          )
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [forType, usingForId],
  )

  const removePermissionsFromRole = useCallback(
    (roleId: string, permissions: string[]) => {
      api.roles.removePermissionsFromRole(
        forType,
        usingForId,
        roleId,
        permissions,
        ({ role: updatedRole }) => {
          setRoles((prevRoles) =>
            prevRoles.map((role) => (role._id === updatedRole._id ? { ...role, ...updatedRole } : role)),
          )
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [forType, usingForId],
  )

  const getRoles = useCallback(() => {
    setLoading(true)
    api.roles.get(
      forType,
      usingForId,
      {},
      (data) => {
        setRoles(data.roles)
        setLoading(false)
      },
      (err) => {
        toast.error(err.message)
        setLoading(false)
      },
    )
  }, [usingForId, forType])

  const createRole = useCallback(() => {
    setCreatingRole(true)
    api.roles.createRole(
      forType,
      usingForId,
      {
        name: newRoleName,
        permissions: newRolePermissions,
      },
      ({ role: newRole }) => {
        setCreatingRole(false)
        setRoles((prevRoles) => [newRole, ...prevRoles])
        setNewRoleName("")
        setNewRolePermissions([])
        setRoleCreationModalOpen(false)
      },
      (err) => {
        toast.error(err.message)
        setCreatingRole(false)
      },
    )
  }, [forType, newRoleName, newRolePermissions, usingForId])

  const deleteRole = useCallback(
    (roleId: string) => {
      util
        .confirm(
          "Delete role",
          `Are you sure you want to delete the role "${roles.find((role) => role._id === roleId).name}"? This is permanent and cannot be undone. All users assigned to this role will have the role revoked and the role will be deleted.`,
        )
        .then(() => {
          setDeletingRole(roleId)
          api.roles.deleteRole(
            forType,
            usingForId,
            roleId,
            () => {
              setManagingPermissionsForRoleModalId("")
              setDeletingRole("")
              setRoles((prevRoles) => prevRoles.filter((role) => role._id !== roleId))
            },
            (err) => {
              toast.error(err.message)
              setDeletingRole("")
            },
          )
        })
        .catch(() => {})
    },
    [forType, roles, usingForId],
  )

  useEffect(() => {
    getRoles()
  }, [getRoles])

  const handleSelectAll = () => {
    if (roleCreationModalOpen) {
      setNewRolePermissions(permissionOptions[0].permissions.map((t) => t.key))
    } else {
      util
        .confirm(
          `Adding all permissions to ${managingRoleModalRole.name}`,
          "Are you sure you want to add all permissions to this role? You can remove them at any time.",
        )
        .then(() => {
          addPermissionsToRole(
            managingRoleModalRole._id,
            permissionOptions[0].permissions.map((t) => t.key),
          )
        })
        .catch(() => {})
    }
  }

  const handleDeselectAll = () => {
    if (roleCreationModalOpen) {
      setNewRolePermissions([])
    } else {
      util
        .confirm(
          `Removing all permissions from ${managingRoleModalRole.name}`,
          "Are you sure you want to remove all permissions from this role? You can add them back at any time.",
        )
        .then(() => {
          removePermissionsFromRole(managingRoleModalRole._id, managingRoleModalRole?.permissions)
        })
        .catch(() => {})
    }
  }

  return (
    <div>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={roleCreationModalOpen}
        onClose={() => setRoleCreationModalOpen(false)}
      >
        <Modal.Header>Create new role</Modal.Header>
        <Modal.Content scrolling>
          <Input placeholder="Role name" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} fluid />
          <Divider hidden />
          {permissionOptions.map((group) => (
            <div key={group.title}>
              {group.permissions.map((permission) => (
                <PermissionDiv key={permission.key}>
                  <PermissionCheckbox>
                    <Checkbox
                      toggle
                      checked={newRolePermissions.includes(permission.key)}
                      onChange={() =>
                        setNewRolePermissions((prevPermissions) => {
                          if (prevPermissions.includes(permission.key)) {
                            return prevPermissions.filter((perm) => perm !== permission.key)
                          }
                          return [...prevPermissions, permission.key]
                        })
                      }
                    />
                  </PermissionCheckbox>
                  <PermissionDetails>
                    <PermissionTitle>{t(permission.title)}</PermissionTitle>
                    <PermissionMeta>{t(permission.text)}</PermissionMeta>
                  </PermissionDetails>
                </PermissionDiv>
              ))}
            </div>
          ))}
        </Modal.Content>

        <Modal.Actions>
          <Checkbox
            style={{ marginRight: 10 }}
            label={newRolePermissions.length > 0 ? "Deselect All" : "Select All"}
            toggle
            checked={newRolePermissions.length > 0}
            onChange={() => {
              if (newRolePermissions.length > 0) {
                handleDeselectAll()
              } else {
                handleSelectAll()
              }
            }}
          />
          <Button content="Cancel" onClick={() => setRoleCreationModalOpen(false)} />
          <Button
            content="Create role"
            icon="plus"
            primary
            onClick={createRole}
            loading={creatingRole}
            disabled={!newRoleName}
          />
        </Modal.Actions>
      </Modal>

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!managingPermissionsForRoleModalId}
        onClose={() => setManagingPermissionsForRoleModalId("")}
      >
        <Modal.Header>
          Managing role "{managingRoleModalRole?.name}"{" "}
          {managingRoleModalRole?.icon?.name ? (
            <Icon name={managingRoleModalRole.icon.name} color={managingRoleModalRole.icon.colour} />
          ) : null}
        </Modal.Header>
        <Modal.Content
          scrolling
          style={{
            minHeight: "80vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Tab
            onTabChange={(e, data) => {
              const tabTitle = data.panes[data.activeIndex]?.menuItem
              setPermissionsTabActive(tabTitle === "Permissions")
            }}
            menu={{ secondary: true, pointing: true }}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
            panes={[
              {
                menuItem: "Settings",
                render: () => (
                  <RoleEditorSettings
                    canSetRolesAsDefault={canSetRolesAsDefault}
                    forType={forType}
                    forId={usingForId}
                    usingRole={managingRoleModalRole}
                    setRoles={setRoles}
                    roleId={managingPermissionsForRoleModalId}
                    roles={roles}
                  />
                ),
              },
              {
                menuItem: "Permissions",
                render: () => (
                  <RoleEditorPermissions
                    removePermissionsFromRole={removePermissionsFromRole}
                    addPermissionsToRole={addPermissionsToRole}
                    forType={forType}
                    forId={usingForId}
                    permissionOptions={permissionOptions}
                    usingRole={managingRoleModalRole}
                    setRoles={setRoles}
                  />
                ),
              },
              managingRoleModalRole?.isDefaultRole
                ? null
                : {
                    menuItem: "Assignment",
                    render: () => (
                      <RoleEditorAssignment
                        forType={forType}
                        forId={usingForId}
                        usingRole={managingRoleModalRole}
                        setRoles={setRoles}
                      />
                    ),
                  },
            ]}
          />
        </Modal.Content>
        <Modal.Actions>
          {permissionsTabActive && !managingRoleModalRole?.preventUserEditing && (
            <Checkbox
              label={managingRoleModalRole?.permissions.length > 0 ? "Deselect All" : "Select All"}
              toggle
              checked={managingRoleModalRole?.permissions.length > 0}
              onChange={() => {
                if (managingRoleModalRole?.permissions.length > 0) {
                  handleDeselectAll()
                } else {
                  handleSelectAll()
                }
              }}
            />
          )}
          {!managingRoleModalRole?.preventUserDeletion ? (
            <Button
              content="Delete role"
              icon="trash"
              basic
              onClick={() => deleteRole(managingPermissionsForRoleModalId)}
              loading={deletingRole === managingPermissionsForRoleModalId}
            />
          ) : null}

          <Button
            content="Done"
            onClick={() => {
              setManagingPermissionsForRoleModalId("")
              setPermissionsTabActive(false)
            }}
          />
        </Modal.Actions>
      </Modal>

      <FilterContainer>
        {canCreateNewRoles ? (
          <Button
            content="Create new role"
            icon="plus"
            primary
            fluid
            style={{ maxWidth: 200 }}
            onClick={() => setRoleCreationModalOpen(true)}
          />
        ) : null}

        <Input
          icon="search"
          placeholder="Search by name..."
          value={roleNameSearch}
          onChange={(e) => setRoleNameSearch(e.target.value)}
        />
        <Input
          icon="search"
          placeholder="Search by permission..."
          value={rolePermissionSearch}
          onChange={(e) => setRolePermissionSearch(e.target.value)}
        />
      </FilterContainer>

      {loading ? (
        <Loader active inline="centered" />
      ) : (
        <RoleTable
          roles={filteredRoles}
          permissionOptions={permissionOptions.flatMap((group) => group.permissions)}
          getRoleActions={(role) => (
            <Button content="Manage" icon="cogs" onClick={() => setManagingPermissionsForRoleModalId(role._id)} />
          )}
        />
      )}
    </div>
  )
}

export default RoleEditor
